import {
  DealActions,
  DealDetail,
  DealServicingStartRollInPlaceBulkArgs,
  DealStatus,
  NegotiationFieldMeta,
  Permission,
  Severity,
  UserPreference,
  ValidationIssue
} from 'api'
import { Locale, Translation } from 'locale'
import React from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from '@emotion/styled'
import { createFilteredDealDetailsSelector } from 'selectors/dealSelectors'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { DialogField, DialogRow } from 'view/components/inputs'
import { ModalFooter, ModalWindow } from 'view/components/panels'
import { canServicingCancel, canServicingSend, getDealModificationColour, getNegotiatedValue } from 'view/routes/deals/components/utils'
import {
  formatHaircutType,
  getCusip,
  getFeeFormatOptions,
  getIsin,
  getNotionalFormatOptions,
  getStartConsiderationFormatOptions
} from '../datagrid'
import { flatten } from 'lodash-es'
import { notNilFilter } from '../../../utils'
import { Content as ModalContent, TableCell, TableHeader, TableRow, TableRows } from './servicingDialogStyles'
import { DealFieldNames } from 'view/routes/deals/components/DealsGridColumnNames'
import { DecisionMakerSelectboxField } from '../entity'
import { getStringModificationType } from 'view/routes/requests/shared/deltaGetters'
import { Permissioned } from 'view/shell/Permissioned'
import { useOnMount } from 'view/hooks'
import { isPropertyEmptyInAll } from './utils'
import { RateCell } from './RateCell'
import { HaircutCell } from './HaircutCell'

export type ServicingRollInPlaceDialogProps = OwnProps & StateProps & DispatchProps

interface OwnProps {
  dealIds: string[]
  onClose?: () => unknown
}

interface StateProps {
  dealDetails: DealDetail[]
  userPreference: UserPreference
}

interface DispatchProps {
  onServicingStartRollInPlaceBulk: (args: DealServicingStartRollInPlaceBulkArgs) => unknown
  onUpdateField: typeof DealActions.servicingUpdateAttributes
  onSend: typeof DealActions.servicingSend
  onCancel: typeof DealActions.servicingCancel
}

const dealIdsSelector = (_: {}, props: ServicingRollInPlaceDialogProps) => props.dealIds

const mapStateToProps = createStructuredSelector({
  dealDetails: createFilteredDealDetailsSelector(dealIdsSelector),
  userPreference: createUserPreferenceSelector()
})

const mapDispatchToProps = {
  onServicingStartRollInPlaceBulk: DealActions.servicingStartRollInPlaceBulk,
  onUpdateField: DealActions.servicingUpdateAttributes,
  onCancel: DealActions.servicingCancel,
  onSend: DealActions.servicingSend
}

const Content = styled(ModalContent)`
  width: 1300px;
`

export const ServicingRollInPlaceDialogComponent: React.FunctionComponent<ServicingRollInPlaceDialogProps> = props => {
  const { dealDetails, userPreference, onServicingStartRollInPlaceBulk, onUpdateField, onSend, onCancel, onClose } = props

  const validationIssues: ValidationIssue[] = flatten(
    dealDetails.map(dealDetail => dealDetail.servicingSideDetails?.validationIssues ?? [])
  ).filter(notNilFilter)
  const hasValidationErrors = validationIssues.filter(validationIssue => validationIssue.severity === Severity.ERROR).length > 0

  const handleSend = () => {
    dealDetails.forEach(dealDetail => {
      onSend(dealDetail.deal.dealId)
    })
    onClose?.()
  }

  const handleCancel = async () => {
    dealDetails.forEach(dealDetail => {
      if (canServicingCancel(dealDetail)) {
        onCancel(dealDetail.deal.dealId)
      }
    })
    onClose?.()
  }

  const handleUpdateField = (dealId: string) => (value: any, field: NegotiationFieldMeta, rawValue?: any) => {
    onUpdateField({
      dealId,
      field,
      updatedValue: value,
      updatedRawValue: rawValue,
      decisionMakerOrientedByNegotiationSide: true,
      meta: { haircutSign: userPreference.haircutSign },
      revertFirst: false
    })
  }

  useOnMount(() => {
    const dealIdsToRollInPlace = dealDetails
      .filter(dealDetail => dealDetail.deal.status !== DealStatus.SERVICING_WITH_INITIATOR)
      .map(dealDetail => dealDetail.deal.dealId)
    if (dealIdsToRollInPlace.length > 0) {
      onServicingStartRollInPlaceBulk({ dealIds: dealIdsToRollInPlace })
    }
  })

  const hideFeeColumn = isPropertyEmptyInAll(dealDetails, dealDetails => dealDetails.deal.fee)
  const hideHaircutColumn = isPropertyEmptyInAll(dealDetails, dealDetails => dealDetails.deal.haircut)
  const hideRateColumn = isPropertyEmptyInAll(dealDetails, dealDetails => dealDetails.deal.rate)
  const hideCusipColumn = isPropertyEmptyInAll(dealDetails, dealDetails => dealDetails.collateral?.cusip)
  const hideSecondaryCollateral = isPropertyEmptyInAll(dealDetails, dealDetails => dealDetails.secondaryInstrumentName)

  return (
    <ModalWindow title={`${Locale.translate(Translation.TC_ROLL_IN_PLACE)} (${dealDetails.length})`} onEscKey={handleCancel}>
      <Content size="big">
        <TableHeader>
          <TableCell flex={2.5} left={true}>
            {Locale.translate(Translation.TC_DEAL_ID)}
          </TableCell>
          <TableCell flex={2.5} left={true}>
            {Locale.translate(Translation.TC_COLLATERAL)}
          </TableCell>
          <TableCell flex={2} left={true}>
            {Locale.translate(Translation.TC_ISIN_BUI)}
          </TableCell>
          {!hideCusipColumn && (
            <TableCell flex={2} left={true}>
              {Locale.translate(Translation.SC_CUSIP)}
            </TableCell>
          )}
          {!hideSecondaryCollateral && (
            <Permissioned oneOf={[Permission.STOCK_BORROWING_LENDING]} allowAdmin={true}>
              <TableCell flex={3} left={true}>
                {Locale.translate(Translation.TC_SECONDARY_COLLATERAL)}
              </TableCell>
            </Permissioned>
          )}
          <TableCell flex={2} left={true}>
            {Locale.translate(Translation.SC_START_DATE)}
          </TableCell>
          <TableCell flex={2} left={true}>
            {Locale.translate(Translation.SC_NOTIONAL, { amountFormat: userPreference.amountFormat })}
          </TableCell>
          <TableCell flex={2.5} left={true}>
            {Locale.translate(Translation.SC_START_CONSIDERATION)}
          </TableCell>
          {!hideRateColumn && (
            <TableCell flex={2} left={true}>
              {Locale.translate(Translation.SC_RATE)}
            </TableCell>
          )}
          {!hideFeeColumn && (
            <Permissioned oneOf={[Permission.STOCK_BORROWING_LENDING]} allowAdmin={true}>
              <TableCell flex={1} left={true}>
                {Locale.translate(Translation.TC_FEE)}
              </TableCell>
            </Permissioned>
          )}
          {!hideHaircutColumn && (
            <TableCell flex={1} left={true}>
              {Locale.translate(Translation.SC_HAIRCUT)}
            </TableCell>
          )}
          {!hideHaircutColumn && (
            <TableCell flex={1.5} left={true}>
              {Locale.translate(Translation.SC_HAIRCUT_TYPE)}
            </TableCell>
          )}
          <TableCell flex={2} left={true}>
            {Locale.translate(Translation.TC_INVESTMENT_DECISION_MAKER_ABBR)}
          </TableCell>
          <TableCell flex={2} left={true}>
            {Locale.translate(Translation.TC_EXECUTION_DECISION_MAKER_ABBR)}
          </TableCell>
        </TableHeader>
        <TableRows>
          {dealDetails.map(dealDetail => {
            const { deal, collateral, referenceRate, ownOrder, collateralBasket } = dealDetail

            const servicingSideDetails = dealDetail.servicingSideDetails
            const sideFields = servicingSideDetails?.sideFields
            const dealValidationIssues = servicingSideDetails?.validationIssues ?? []
            const onUpdate = handleUpdateField(dealDetail.deal.dealId)

            const idmField = dealDetail.ownOrder
              ? NegotiationFieldMeta.INITIATOR_INVESTMENT_DECISION_MAKER
              : NegotiationFieldMeta.RESPONDER_INVESTMENT_DECISION_MAKER
            const edmField = dealDetail.ownOrder
              ? NegotiationFieldMeta.INITIATOR_EXECUTION_DECISION_MAKER
              : NegotiationFieldMeta.RESPONDER_EXECUTION_DECISION_MAKER

            return (
              <TableRow key={dealDetail.deal.dealId}>
                <TableCell flex={2.5} left={true}>
                  {dealDetail.deal.dealId}
                </TableCell>
                <TableCell flex={2.5} left={true}>
                  {collateral?.name ?? collateralBasket?.name}
                </TableCell>
                <TableCell flex={2} left={true}>
                  {dealDetail ? getIsin(dealDetail) : ''}
                </TableCell>
                {!hideCusipColumn && (
                  <TableCell flex={2} left={true}>
                    {dealDetail ? getCusip(dealDetail) : ''}
                  </TableCell>
                )}
                {!hideSecondaryCollateral && (
                  <Permissioned oneOf={[Permission.STOCK_BORROWING_LENDING]} allowAdmin={true}>
                    <TableCell flex={3} left={true}>
                      {dealDetail ? dealDetail.secondaryInstrumentName : ''}
                    </TableCell>
                  </Permissioned>
                )}
                <TableCell flex={2} left={true}>
                  <span>{Locale.formatDate(deal.startDate, userPreference.dateFormat)}</span>
                </TableCell>
                <TableCell flex={2} left={true}>
                  <span>{Locale.formatNumber(deal.notional, getNotionalFormatOptions(userPreference.amountFormat))}</span>
                </TableCell>
                <TableCell flex={2.5} left={true}>
                  <span>{Locale.formatNumber(deal.startConsideration, getStartConsiderationFormatOptions())}</span>
                </TableCell>

                <RateCell hideRateColumn={hideRateColumn} deal={deal} referenceRate={referenceRate} left={true} />
                {!hideFeeColumn && (
                  <Permissioned oneOf={[Permission.STOCK_BORROWING_LENDING]} allowAdmin={true}>
                    <TableCell flex={1} left={true}>
                      <span>{Locale.formatNumber(deal.fee, getFeeFormatOptions())}</span>
                    </TableCell>
                  </Permissioned>
                )}

                <HaircutCell
                  hideHaircutColumn={hideHaircutColumn}
                  deal={deal}
                  haircutSign={userPreference.haircutSign}
                  ownOrder={ownOrder}
                  testId="ServicingRollInPlaceDialog-HaircutTableCell"
                  left={true}
                />

                {!hideHaircutColumn && (
                  <TableCell flex={1.5} left={true}>
                    {deal.haircutType ? formatHaircutType(deal.haircutType) : ''}
                  </TableCell>
                )}
                <TableCell flex={2} left={true}>
                  <DecisionMakerSelectboxField
                    label={''}
                    fieldName={idmField}
                    validationField={
                      dealDetail.ownOrder
                        ? DealFieldNames.DEAL_INITIATOR_INVESTMENT_DECISION_MAKER
                        : DealFieldNames.DEAL_RESPONDER_INVESTMENT_DECISION_MAKER
                    }
                    validationIssues={dealValidationIssues}
                    underline={getDealModificationColour(
                      () => getStringModificationType(sideFields?.[idmField]?.negotiableFieldRequired),
                      dealDetail
                    )}
                    value={getNegotiatedValue(sideFields?.[idmField]?.negotiableFieldRequired)}
                    onUpdate={onUpdate}
                    dataTestId={'ServicingRollInPlaceDialog-DecisionMakerSelectboxField-Investment'}
                  />
                </TableCell>
                <TableCell flex={2} left={true}>
                  <DecisionMakerSelectboxField
                    label={''}
                    fieldName={edmField}
                    validationField={
                      dealDetail.ownOrder
                        ? DealFieldNames.DEAL_INITIATOR_EXECUTION_DECISION_MAKER
                        : DealFieldNames.DEAL_RESPONDER_EXECUTION_DECISION_MAKER
                    }
                    validationIssues={dealValidationIssues}
                    underline={getDealModificationColour(
                      () => getStringModificationType(sideFields?.[edmField]?.negotiableFieldRequired),
                      dealDetail
                    )}
                    value={getNegotiatedValue(sideFields?.[edmField]?.negotiableFieldRequired)}
                    onUpdate={onUpdate}
                    dataTestId={'ServicingRollInPlaceDialog-DecisionMakerSelectboxField-Execution'}
                  />
                </TableCell>
              </TableRow>
            )
          })}
        </TableRows>
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton type={TextButtonType.Tertiary} onClick={handleCancel} dataTestId={'ServicingDialog-CancelButton'}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              type={TextButtonType.Primary}
              disabled={hasValidationErrors || dealDetails.some(d => !canServicingSend(d))}
              onClick={handleSend}
              dataTestId={'ServicingRollInPlaceDialog-SendRequestButton'}>
              {Locale.translate(Translation.TC_BUTTON_SEND_REQUEST)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

export const ServicingRollInPlaceDialog = connect<StateProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps
)(ServicingRollInPlaceDialogComponent)
